import L from 'leaflet';
import { LAYERS_URLS } from './mapUrls';

// own offline maps: MBTiles file -> IndexedDB tiles; plus read-only layer over already saved offline areas
const DB = 'GissOwnMaps', T = 'tiles', M = 'maps';
let dbp = null;
const db = () => (dbp ||= new Promise((res, rej) => {
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => { r.result.createObjectStore(T); r.result.createObjectStore(M, { keyPath: 'id' }); };
  r.onsuccess = () => res(r.result); r.onerror = rej;
}));
const req = (q) => new Promise((res, rej) => { q.onsuccess = () => res(q.result); q.onerror = rej; });
const emit = () => window.dispatchEvent(new Event('giss-ownmaps'));

export const listMaps = async () => req((await db()).transaction(M).objectStore(M).getAll());
export async function deleteMap(id) {
  const d = await db(), tx = d.transaction([T, M], 'readwrite');
  tx.objectStore(T).delete(IDBKeyRange.bound(`${id}/`, `${id}/\uffff`));
  tx.objectStore(M).delete(id);
  await new Promise((r) => { tx.oncomplete = r; });
  emit();
}

const loadSql = () => window.initSqlJs ? window.initSqlJs({ locateFile: (f) => `/sqljs/${f}` }) : new Promise((res, rej) => {
  const s = document.createElement('script'); s.src = '/sqljs/sql-wasm.js';
  s.onload = () => window.initSqlJs({ locateFile: (f) => `/sqljs/${f}` }).then(res, rej); s.onerror = rej;
  document.head.appendChild(s);
});

// MBTiles (SQLite, TMS rows) -> tiles keyed "<map>/<z>/<x>/<y>" (XYZ)
export async function importMbtiles(file, onProgress) {
  const SQL = await loadSql(), sdb = new SQL.Database(new Uint8Array(await file.arrayBuffer()));
  try {
    const meta = Object.fromEntries((sdb.exec('SELECT name, value FROM metadata')[0]?.values || []));
    const fmt = (meta.format || 'png').toLowerCase();
    if (fmt === 'pbf') throw new Error('vector');
    const id = `mbt_${Date.now().toString(36)}`, type = fmt === 'jpg' || fmt === 'jpeg' ? 'image/jpeg' : fmt === 'webp' ? 'image/webp' : 'image/png';
    const total = sdb.exec('SELECT COUNT(*) FROM tiles')[0].values[0][0];
    if (!total) throw new Error('empty');
    const st = sdb.prepare('SELECT zoom_level, tile_column, tile_row, tile_data FROM tiles');
    let n = 0, batch = [], minZ = 99, maxZ = 0;
    const flush = async () => { const tx = (await db()).transaction(T, 'readwrite'), s = tx.objectStore(T); batch.forEach(([k, v]) => s.put(v, k)); await new Promise((r) => { tx.oncomplete = r; }); batch = []; onProgress?.(n / total); };
    while (st.step()) {
      const [z, x, row, data] = st.get();
      minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
      batch.push([`${id}/${z}/${x}/${(1 << z) - 1 - row}`, new Blob([data], { type })]);
      n++;
      if (batch.length >= 500) await flush();
    }
    st.free();
    if (batch.length) await flush();
    const b = (meta.bounds || '').split(',').map(Number);
    const m = { id, name: meta.name || file.name.replace(/\.mbtiles$/i, ''), tiles: n, minZ, maxZ, bounds: b.length === 4 && b.every(Number.isFinite) ? b : null, size: file.size, at: Date.now() };
    await req((await db()).transaction(M, 'readwrite').objectStore(M).put(m));
    emit();
    return m;
  } finally { sdb.close(); }
}

const blankTile = () => { const c = document.createElement('canvas'); c.width = c.height = 256; return c; };
const blobTile = (blob, done) => {
  if (!blob) { const c = blankTile(); setTimeout(() => done(null, c), 0); return c; }
  const img = document.createElement('img'), u = URL.createObjectURL(blob);
  img.onload = () => { URL.revokeObjectURL(u); done(null, img); }; img.onerror = () => done(null, img);
  img.src = u;
  return img;
};

// tiles from an imported MBTiles map
const IdbLayer = L.GridLayer.extend({
  createTile(c, done) {
    const tile = document.createElement('div');
    db().then((d) => req(d.transaction(T).objectStore(T).get(`${this.options.mapId}/${c.z}/${c.x}/${c.y}`))).then((b) => tile.appendChild(blobTile(b, () => done(null, tile))), () => done(null, tile));
    return tile;
  },
});
// tiles only from the offline cache (saved areas) - never from the network
const CacheLayer = L.GridLayer.extend({
  createTile(c, done) {
    const tile = document.createElement('div');
    (async () => {
      const cache = await caches.open('tiles-v1');
      for (const tpl of LAYERS_URLS) {
        const hit = await cache.match(L.Util.template(tpl, { ...c, s: 'a' }), { ignoreVary: true });
        if (hit) return hit.blob();
      }
      return null;
    })().then((b) => tile.appendChild(blobTile(b, () => done(null, tile))), () => done(null, tile));
    return tile;
  },
});

export function ownLayer(key, map) {
  if (key === 'cache') return new CacheLayer({ maxZoom: 24, maxNativeZoom: 20 });
  if (key.startsWith('mbt_')) return new IdbLayer({ mapId: key, maxZoom: 24, maxNativeZoom: map?.maxZ || 18, minNativeZoom: map?.minZ || 0 });
  return null;
}
